import { ReactElement, ReactNode } from "react";
import { Dialog, DialogContent, DialogActions, DialogTitle } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { useWhiteLabelContext } from "../../../../WhiteLabelContext";

interface Props {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  destructive,
  onCancel,
  onConfirm,
}: Props): ReactElement {
  const { whiteLabel } = useWhiteLabelContext();
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "4px",
          border: "1px solid rgb(226 232 240)",
          boxShadow: "0 4px 24px -8px rgb(15 23 42 / 0.18)",
          "--primary": whiteLabel.colorHex,
          "--primary-bg": `${whiteLabel.colorHex}10`,
        },
      }}
    >
      <DialogTitle className="!pb-2 flex items-start gap-3">
        <span
          className={`flex-shrink-0 mt-1 h-7 w-7 rounded-sm border flex items-center justify-center ${
            destructive
              ? "bg-amber-50 border-amber-200 text-amber-700"
              : "bg-slate-100 border-slate-200 text-slate-700"
          }`}
        >
          <FontAwesomeIcon icon={faTriangleExclamation} className="text-xs" />
        </span>
        <span>
          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold block">
            Bekræft
          </span>
          <span className="text-base font-semibold tracking-tight text-slate-900 block mt-0.5">
            {title}
          </span>
        </span>
      </DialogTitle>
      <DialogContent className="!pt-2 !pb-4">
        <div className="text-sm text-slate-600 leading-relaxed">{body}</div>
      </DialogContent>
      <DialogActions className="!px-6 !py-4 !border-t !border-slate-200">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900"
        >
          Annullér
        </button>
        <button
          onClick={onConfirm}
          className={`px-4 py-1.5 text-xs font-semibold text-white rounded-sm transition-all ${
            destructive
              ? "bg-amber-600 hover:bg-amber-700"
              : "bg-success hover:brightness-95"
          }`}
        >
          {confirmLabel}
        </button>
      </DialogActions>
    </Dialog>
  );
}

export function isAdminScope(scope: string): boolean {
  return scope.toLowerCase().startsWith("admin.");
}
